/**
 * The V1 mutation set (`specs/M4-V1-views.md` §6, §8).
 *
 * B1's `useViewMutations` already covers create / rename / duplicate / delete / reorder. This
 * hook adds what the captures turned up: lock, pin, hide, publish and the **per-user** tab
 * order behind monday's "Reorder (for you only)".
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation } from '../boards/hooks';

/** The caller's own tab-order overrides for one board: `{ viewId: sortKey }`. */
export function usePersonalViewOrder(boardId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.viewsAdmin.personalOrder.queryOptions({ boardId: boardId ?? '0' }),
    enabled: boardId !== null,
    staleTime: 30_000,
  });
}

export function useViewAdminMutations(boardId: string) {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();

  const lock = useMutation({
    mutationFn: (input: { viewId: string; locked: boolean }) =>
      client.viewsAdmin.lock.mutate({ boardId, ...input }),
    onSuccess: invalidate,
  });
  const pin = useMutation({
    mutationFn: (input: { viewId: string; pinned: boolean }) =>
      client.viewsAdmin.pin.mutate({ boardId, ...input }),
    onSuccess: invalidate,
  });
  const hide = useMutation({
    mutationFn: (input: { viewId: string | null; hidden: boolean }) =>
      client.viewsAdmin.hide.mutate({ boardId, ...input }),
    onSuccess: invalidate,
  });
  const publish = useMutation({
    mutationFn: (input: { viewId: string; regenerate?: boolean }) =>
      client.viewsAdmin.publish.mutate({ boardId, regenerate: false, ...input }),
    onSuccess: invalidate,
  });
  const unpublish = useMutation({
    mutationFn: (viewId: string) => client.viewsAdmin.unpublish.mutate({ boardId, viewId }),
    onSuccess: invalidate,
  });
  /** Per user: it writes no board event and no activity row (§6). */
  const reorderForMe = useMutation({
    mutationFn: (input: { viewId: string; method: 'back' | 'ahead' | 'first' | 'last' }) =>
      client.viewsAdmin.reorderForMe.mutate({ boardId, ...input }),
    onSuccess: invalidate,
  });

  return { lock, pin, hide, publish, unpublish, reorderForMe };
}
